'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { CartLine } from '@/lib/types'

interface CartContextValue {
  lines: CartLine[]
  totalQuantity: number
  subtotalCents: number
  add: (line: CartLine) => void
  updateQuantity: (productId: string, variantId: string | null, qty: number) => void
  remove: (productId: string, variantId: string | null) => void
  clear: () => void
}

const CartContext = createContext<CartContextValue | null>(null)
const STORAGE_KEY = 'veterans-shop-cart-v1'

function lineKey(productId: string, variantId: string | null): string {
  return `${productId}::${variantId ?? ''}`
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  // Hydrate synchronously via lazy initializer — the Vite SPA has no SSR pass,
  // so reading localStorage at first render is safe. This avoids the empty-cart
  // flash and the sync-setState-in-effect pattern the old hydration effect had.
  const [lines, setLines] = useState<CartLine[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as CartLine[]
        if (Array.isArray(parsed)) return parsed
      }
    } catch {
      // ignore corrupt cart
    }
    return []
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lines))
    } catch {
      // ignore quota errors
    }
  }, [lines])

  const add = useCallback((line: CartLine) => {
    setLines((prev) => {
      const key = lineKey(line.productId, line.variantId)
      const idx = prev.findIndex((l) => lineKey(l.productId, l.variantId) === key)
      if (idx >= 0) {
        const next = [...prev]
        next[idx] = { ...next[idx], quantity: next[idx].quantity + line.quantity }
        return next
      }
      return [...prev, line]
    })
  }, [])

  const updateQuantity = useCallback((productId: string, variantId: string | null, qty: number) => {
    setLines((prev) => {
      const key = lineKey(productId, variantId)
      if (qty <= 0) {
        return prev.filter((l) => lineKey(l.productId, l.variantId) !== key)
      }
      return prev.map((l) =>
        lineKey(l.productId, l.variantId) === key ? { ...l, quantity: qty } : l,
      )
    })
  }, [])

  const remove = useCallback((productId: string, variantId: string | null) => {
    setLines((prev) =>
      prev.filter((l) => lineKey(l.productId, l.variantId) !== lineKey(productId, variantId)),
    )
  }, [])

  const clear = useCallback(() => setLines([]), [])

  const totalQuantity = useMemo(
    () => lines.reduce((acc, l) => acc + l.quantity, 0),
    [lines],
  )
  const subtotalCents = useMemo(
    () => lines.reduce((acc, l) => acc + l.unitPriceCents * l.quantity, 0),
    [lines],
  )

  const value = useMemo<CartContextValue>(
    () => ({ lines, totalQuantity, subtotalCents, add, updateQuantity, remove, clear }),
    [lines, totalQuantity, subtotalCents, add, updateQuantity, remove, clear],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext)
  if (!ctx) {
    // Pre-mount safe defaults so SSR doesn't blow up.
    return {
      lines: [],
      totalQuantity: 0,
      subtotalCents: 0,
      add: () => {},
      updateQuantity: () => {},
      remove: () => {},
      clear: () => {},
    }
  }
  return ctx
}
